import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/constants";

/* Shared between the patient chart and the invoice forms.
 *
 * These started life inside PatientTimeline, where the chart's completed
 * procedures are entered. The invoice screens needed the same three things —
 * the catalogue behind a search, one way of writing an amount, one way of
 * pricing a line — and a second copy of the arithmetic is the one thing that
 * cannot be allowed: a chart and the bill raised from it disagreeing about
 * what a discount does is worse than either being wrong on its own. */

// Bare figure, two decimals, for tables whose column heading already carries
// the ₹ — repeating the symbol on every cell only makes the column harder to
// scan down.
export const amt = (n) => (Number(n) || 0).toLocaleString("en-IN",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Money rounds to paise at every step, the way the backend does. Left to float
// drift, a line total shows 3716.9999999999995 and the chart stops agreeing
// with the invoice.
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/* Line money, computed exactly as the backend's `_price_item` does — discount
 * absolute and applied before tax, both rounded at the same points. A total
 * that drifts from the server's by a paisa is the sort of thing a receptionist
 * notices and stops trusting the screen over.
 *
 * `discount_type` is a UI affordance only: a percentage is HOW the concession
 * was decided, the rupees are WHAT came off. Only the rupees are ever stored,
 * because re-deriving a percentage later against a rate that has since changed
 * would quietly restate what the patient was charged. */
export function lineMoney(r) {
  const gross = round2((Number(r.rate) || 0) * (Number(r.quantity) || 1));
  const raw = r.discount_type === "percent"
    ? gross * (Number(r.discount) || 0) / 100
    : (Number(r.discount) || 0);
  const discount = Math.min(round2(Math.max(0, raw)), gross);
  const net = round2(gross - discount);
  const tax = round2(net * (Number(r.tax_percent) || 0) / 100);
  return { gross, discount, net, tax, total: round2(net + tax) };
}

const label = (o) => (typeof o === "string" ? o : o.name);

/* A text box that suggests as you type but never insists — the value is always
 * whatever is in the field, so an unusual frequency is one the doctor can just
 * write. `footer` is how the drug search offers "Add New Drug". */
export function Suggest({ value, options, placeholder, onChange, onPick, footer,
                         renderOption, emptyText = "No match.", className = "" }) {
  const [open, setOpen] = useState(false);
  const box = React.useRef(null);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const q = (value || "").trim().toLowerCase();
  const hits = q ? options.filter((o) => label(o).toLowerCase().includes(q)) : options;

  return (
    <div ref={box} className="relative">
      <Input className={`h-8 bg-white text-xs ${className}`} placeholder={placeholder} value={value}
             onFocus={() => setOpen(true)}
             onChange={(e) => { onChange(e.target.value); setOpen(true); }} />
      {/* Rendered even when it has nothing to offer: an empty box that says so
          is what tells you the catalogue is empty rather than the search
          broken. */}
      {open && (
        <div className="absolute left-0 z-50 mt-1 max-h-56 w-full min-w-[13rem] overflow-y-auto rounded-lg border border-navy/15 bg-white py-1 shadow-lg">
          {hits.slice(0, 40).map((o, i) => (
            <button key={o.id || `${label(o)}-${i}`} type="button"
                    onClick={() => { (onPick || onChange)(o); setOpen(false); }}
                    className="block w-full px-3 py-1.5 text-left text-xs text-ink hover:bg-nude/50">
              {renderOption ? renderOption(o) : label(o)}
            </button>
          ))}
          {hits.length === 0 && <p className="px-3 py-2 text-center text-xs text-muted-foreground">{emptyText}</p>}
          {footer && <div className="border-t border-navy/10 pt-1">{footer(() => setOpen(false))}</div>}
        </div>
      )}
    </div>
  );
}

/* The box under the list. Ray puts the catalogue behind a search rather than a
 * dropdown because a clinic's price list runs to hundreds of lines, and it
 * still accepts a name that is not on it — the walk-in extra that gets added to
 * the catalogue properly later, or never. */
export function ProcedureSearch({ catalog, onPick, className = "w-full sm:w-64" }) {
  const [q, setQ] = useState("");
  return (
    <Suggest value={q} options={catalog} className={className}
             placeholder="Enter Procedure Name" emptyText="No Recent Procedures"
             onChange={setQ}
             // Snapshot name, price and tax off the catalogue at the moment of
             // picking. The line is what the patient was quoted today; a price
             // change next month must not rewrite it.
             onPick={(p) => {
               onPick({ package_id: p.id, name: p.name, rate: p.price ?? "",
                        tax_percent: p.tax_percent ?? "" });
               setQ("");
             }}
             renderOption={(p) => (
               <span className="flex items-baseline justify-between gap-3">
                 <span className="truncate">{p.name}</span>
                 <span className="shrink-0 text-muted-foreground">{formatCurrency(p.price)}</span>
               </span>
             )}
             footer={q.trim() ? (close) => (
               <button type="button"
                       onClick={() => { onPick({ name: q.trim() }); setQ(""); close(); }}
                       className="block w-full px-3 py-1.5 text-left text-xs font-medium text-sky-600 hover:bg-nude/50">
                 Add “{q.trim()}” as a one-off
               </button>
             ) : null} />
  );
}
